import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { STAGES, STAGE_LABEL, canUploadWO, normalizeStage } from '@/lib/roles';
import { useAuth } from '@/lib/auth';
import { MaskedAmount } from '@/components/common/MaskedAmount';
import { fmtDate } from '@/lib/format';
import { Plus, LayoutGrid, List as ListIcon, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const STAGE_COLORS = [
  'hsl(var(--primary))', 'hsl(var(--info))', 'hsl(var(--warning))',
  'hsl(var(--success))', 'hsl(var(--neutral))', 'hsl(var(--foreground-subtle))',
];

const StageStrip = ({ projects }) => {
  if (projects.length === 0) return null;
  const counts = {};
  projects.forEach(p => {
    const s = normalizeStage(p.current_stage);
    counts[s] = (counts[s] || 0) + 1;
  });
  const items = STAGES
    .map((s, i) => ({ key: s.key, label: s.label, count: counts[s.key] || 0, color: STAGE_COLORS[i % STAGE_COLORS.length] }))
    .filter(x => x.count > 0);
  return (
    <div className="card p-3 mb-4" data-testid="projects-stage-strip">
      <div className="h-2.5 rounded-full flex overflow-hidden" style={{ background: 'hsl(var(--surface-2))' }}>
        {items.map(i => (
          <div key={i.key} title={`${i.label}: ${i.count}`}
               style={{ width: `${(i.count / projects.length) * 100}%`, background: i.color }} />
        ))}
      </div>
      <div className="flex items-center gap-3 mt-2 text-[11.5px] text-[hsl(var(--foreground-muted))] flex-wrap">
        {items.map(i => (
          <span key={i.key} className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: i.color }} />
            {i.label} <span className="tabular-nums font-medium">{i.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState('board');
  const isOwner = user?.role === 'owner';

  const load = () =>
    api.get('/projects').then(r => setProjects(r.data || [])).catch(e => toast.error(parseErr(e)));

  useEffect(() => { load(); }, []);

  const byStage = Object.fromEntries(STAGES.map(s => [s.key, []]));
  projects.forEach(p => { byStage[normalizeStage(p.current_stage)].push(p); });

  const total = (p) => (p.milestones || []).reduce((s, m) => s + (m.amount_inr || 0), 0);

  return (
    <AppShell breadcrumbs={[{ label: 'Projects' }]}>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="projects-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Projects</h1>
            <p className="text-[hsl(var(--foreground-muted))] text-[13px]">{projects.length} project{projects.length === 1 ? '' : 's'} · 6-stage pipeline</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border rounded-md overflow-hidden" style={{ borderColor: 'hsl(var(--border))' }}>
              <button onClick={() => setView('board')} className={`px-3 h-8 text-[12.5px] flex items-center gap-1 ${view === 'board' ? 'bg-[hsl(var(--surface-2))]' : ''}`} data-testid="projects-view-board">
                <LayoutGrid size={13} /> Board
              </button>
              <button onClick={() => setView('list')} className={`px-3 h-8 text-[12.5px] flex items-center gap-1 ${view === 'list' ? 'bg-[hsl(var(--surface-2))]' : ''}`} data-testid="projects-view-list">
                <ListIcon size={13} /> List
              </button>
            </div>
            {canUploadWO(user.role) && (
              <Button asChild size="sm" data-testid="projects-new-wo">
                <Link to="/work-orders/upload"><Plus size={14} className="mr-1" />New from Work Order</Link>
              </Button>
            )}
          </div>
        </div>

        <StageStrip projects={projects} />

        {view === 'board' ? (
          <div data-testid="kanban-board" className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-x-auto pb-4">
            {STAGES.map(s => (
              <div key={s.key} className="kb-col" data-testid={`kanban-column-${s.key}`}>
                <div className="kb-col-header">
                  <div className="kb-col-title">{s.label}</div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] tabular-nums">{byStage[s.key].length}</div>
                </div>
                <div className="flex-1">
                  {byStage[s.key].length === 0 && <div className="text-[12px] text-[hsl(var(--foreground-subtle))] italic px-1 py-6 text-center border border-dashed rounded-md" style={{ borderColor: 'hsl(var(--border))' }}>Empty</div>}
                  {byStage[s.key].map(p => (
                    <div key={p.id} className="kb-card group relative" data-testid={`kanban-card-${p.id}`}>
                      <Link to={`/projects/${p.id}`} className="block">
                        <div className="text-[13.5px] font-medium">
                          {p.name}
                          {p.stage_defaulted && (
                            <span className="chip chip-warning text-[9.5px] ml-1 align-middle"
                                  title="No stage set — bucketed in Strategy & Concept. Open the project to set a stage.">UNSTAGED</span>
                          )}
                        </div>
                        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5 truncate">{p.client_name}</div>
                        <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-2 font-mono">{p.code}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="chip chip-info">{(p.milestones || []).length} milestones</span>
                          <MaskedAmount amount={total(p)} masked={(p.milestones || [])[0]?.amount_masked} />
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Project</th><th>Client</th><th>Stage</th><th>Code</th>
                  <th>Milestones</th><th>Total</th><th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id} data-testid={`project-row-${p.id}`}>
                    <td><Link to={`/projects/${p.id}`} className="underline">{p.name}</Link></td>
                    <td>{p.client_name}</td>
                    <td>
                      <span className="chip chip-info">{STAGE_LABEL[normalizeStage(p.current_stage)]}</span>
                      {p.stage_defaulted && (
                        <span className="chip chip-warning text-[10px] ml-1"
                              title="No stage set — defaulted to Strategy & Concept. Update it in Project Detail.">UNSTAGED</span>
                      )}
                    </td>
                    <td className="font-mono text-[12px]">{p.code}</td>
                    <td>{(p.milestones || []).length}</td>
                    <td><MaskedAmount amount={total(p)} masked={(p.milestones || [])[0]?.amount_masked} /></td>
                    <td className="text-[12px] text-[hsl(var(--foreground-muted))]">{fmtDate(p.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
